import { Link } from "react-router-dom";
import { Wallet, ExternalLink } from "lucide-react";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { formatDate, formatMXN } from "@/lib/utils";
import { useListFacturas } from "./api";
import { ESTATUS_LABEL, ESTATUS_VARIANT } from "./helpers";

type Props = {
  contratoId: string;
  /** Label del contrato (codigo) para el breadcrumb back en FacturaDetailPage. */
  fromLabel?: string;
};

export function ContratoFacturasTab({ contratoId, fromLabel }: Props) {
  const { data, isLoading } = useListFacturas({
    contrato_id: contratoId,
    size: 100,
  });
  const items = data?.items ?? [];

  // Sumatoria rápida de saldo pendiente del contrato (facturas no anuladas)
  const saldoPendiente = items
    .filter((f) => f.estatus !== "anulada")
    .reduce((acc, f) => acc + Number(f.saldo), 0);

  return (
    <div className="flex flex-col gap-4">
      {/* Strip resumen del contrato */}
      {items.length > 0 && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          <StatCard label="Facturas emitidas" value={String(items.length)} />
          <StatCard
            label="Saldo pendiente"
            value={formatMXN(saldoPendiente)}
            variant={saldoPendiente > 0 ? "destructive" : "success"}
          />
          <StatCard
            label="Vencidas"
            value={String(items.filter((f) => f.estatus === "vencida").length)}
            variant={
              items.some((f) => f.estatus === "vencida") ? "destructive" : undefined
            }
          />
        </div>
      )}

      {/* Tabla */}
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Folio</TableHead>
              <TableHead>Emisión</TableHead>
              <TableHead>Vencimiento</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
              <TableHead>Estatus</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={Wallet}
                  title="Sin facturas en este contrato"
                  description="Aún no se ha generado ninguna factura para este contrato. El botón 'Generar factura' llega en F20.2."
                />
              </TableEmpty>
            ) : (
              items.map((f) => (
                <TableRow key={f.id}>
                  <TableCell className="font-mono text-xs">
                    <div className="flex items-center gap-1.5">
                      {f.folio}
                      {f.tipo === "libre" && (
                        <Badge variant="outline" className="border-blue-300 bg-blue-50 text-[10px] text-blue-800">
                          Servicio adicional
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{formatDate(f.fecha_emision)}</TableCell>
                  <TableCell className="text-xs">
                    {formatDate(f.fecha_vencimiento)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatMXN(f.total)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {Number(f.saldo) > 0 ? (
                      <span className="text-destructive">{formatMXN(f.saldo)}</span>
                    ) : (
                      <span className="text-muted-foreground">{formatMXN(0)}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={ESTATUS_VARIANT[f.estatus]}>
                      {ESTATUS_LABEL[f.estatus]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/facturas/${f.id}`}
                      state={
                        fromLabel
                          ? { from: `/contratos/${contratoId}`, fromLabel }
                          : undefined
                      }
                      className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                    >
                      Ver
                      <ExternalLink size={10} />
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatCard({
  label, value, variant,
}: {
  label: string;
  value: string;
  variant?: "destructive" | "success";
}) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border bg-card p-3">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={[
          "text-base leading-none font-semibold",
          variant === "destructive" ? "text-destructive" : "",
          variant === "success" ? "text-emerald-600 dark:text-emerald-400" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {value}
      </span>
    </div>
  );
}
